import { Injectable } from '@angular/core';
import * as THREE from 'three';
import * as htmlToImage from 'html-to-image';
import { ShapeTypeEnum } from '../../../../../enum/workbench/shape-type.enum';

@Injectable({
  providedIn: 'root'
})
export class WorkbenchSceneService {

  public container: HTMLElement;
  public renderer: THREE.WebGLRenderer;
  public scene: THREE.Scene;

  /** Active camera — OrthographicCamera in 2D, PerspectiveCamera in 3D. */
  public camera: THREE.OrthographicCamera | THREE.PerspectiveCamera;

  /** The permanent orthographic camera used in 2D view. */
  private orthoCamera: THREE.OrthographicCamera;

  /** Perspective camera instantiated on first 3D activation and reused. */
  private perspCamera: THREE.PerspectiveCamera | null = null;

  // Animation State
  private animationId: number;
  public is3DMode = false;
  public isTransitioning = false;
  private debugAngle = 0;

  // 3D orbit params (computed when entering 3D mode)
  private debugRadius = 1000;
  private debugHeight = 800;
  private debugCenter: THREE.Vector3 = new THREE.Vector3(0, 0, 0);

  constructor() { }

  // ---------------------------------------------------------------------------
  // Init / Destroy
  // ---------------------------------------------------------------------------

  public init(container: HTMLElement): void {
    this.container = container;
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    this.renderer.setSize(width, height);
    this.renderer.setClearColor(0xffffff, 1);
    this.container.appendChild(this.renderer.domElement);

    // Scene
    this.scene = new THREE.Scene();

    // Orthographic Camera — frustum bounds are set once by zoomToFit.
    // Use a sensible half-size until the first zoomToFit call.
    const aspect = width / height;
    const halfH = height / 2; // 1 unit = 1 pixel initially
    this.orthoCamera = new THREE.OrthographicCamera(
      -halfH * aspect, halfH * aspect,
      halfH, -halfH,
      -10000, 10000
    );
    this.orthoCamera.position.set(0, 0, 1000);
    this.orthoCamera.up.set(0, 1, 0);
    this.orthoCamera.lookAt(0, 0, 0);

    this.camera = this.orthoCamera;
  }

  public destroy() {
    cancelAnimationFrame(this.animationId);
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement?.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Animation Loop
  // ---------------------------------------------------------------------------

  public startAnimationLoop(renderCallback: () => boolean | void): void {
    this.animate(renderCallback);
  }

  private animate(renderCallback: () => boolean | void) {
    this.animationId = requestAnimationFrame(() => this.animate(renderCallback));

    if (this.is3DMode && !this.isTransitioning) {
      this.animate3DLogic();
    }

    let handled = false;
    if (renderCallback) {
      handled = !!renderCallback();
    }
    if (!handled) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  // ---------------------------------------------------------------------------
  // Resize
  // ---------------------------------------------------------------------------

  public resize(width: number, height: number) {
    if (!this.container || !this.renderer) return;
    this.renderer.setSize(width, height);
    this.updateOrthoCameraAspect(width, height);
  }

  /**
   * Keep ortho frustum symmetric around its current center while preserving
   * the vertical world-space size (camera.zoom handles the logical zoom level).
   */
  private updateOrthoCameraAspect(width: number, height: number) {
    const cam = this.orthoCamera;
    const aspect = width / height;
    const halfH = (cam.top - cam.bottom) / 2;
    const halfW = halfH * aspect;
    cam.left = -halfW;
    cam.right = halfW;
    cam.updateProjectionMatrix();

    if (this.perspCamera) {
      this.perspCamera.aspect = aspect;
      this.perspCamera.updateProjectionMatrix();
    }
  }

  // ---------------------------------------------------------------------------
  // Camera Controls — Orthographic helpers
  // ---------------------------------------------------------------------------

  /**
   * Sets the orthographic frustum so that all plans fit inside the viewport
   * with the given padding ratio. Resets zoom to 1.
   */
  public zoomToFit(plans: THREE.Object3D[], safeArea?: { top?: number; left?: number }) {
    const vhPx = this.container?.clientHeight || 600;

    const { halfW: baseHalfW, halfH: baseHalfH, center } = this.getFitOrthoParams(plans, 1.5);
    let halfW = baseHalfW;
    let halfH = baseHalfH;

    const safeTop = safeArea?.top ?? 0;
    if (safeTop > 0) {
      // Available height after subtracting the legend zone at the top
      const availH = Math.max(vhPx - safeTop, 1);

      // Scale frustum uniformly so content fits within the available height
      const scale = vhPx / availH;
      halfW *= scale;
      halfH *= scale;

      // Shift camera up so content is centered in the available (sub-legend) area.
      // The available area center is safeTop/2 pixels below the viewport center.
      const worldPerPxY = (halfH * 2) / vhPx;
      center.y += (safeTop / 2) * worldPerPxY; // camera up → content appears lower in viewport
    }

    const cam = this.orthoCamera;
    cam.left   = -halfW;
    cam.right  =  halfW;
    cam.top    =  halfH;
    cam.bottom = -halfH;
    cam.zoom   = 1;
    cam.position.set(center.x, center.y, 1000);
    cam.up.set(0, 1, 0);
    cam.lookAt(center.x, center.y, 0);
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
  }

  private getFitOrthoParams(plans: THREE.Object3D[], padding = 1.5): { halfW: number, halfH: number, center: THREE.Vector3 } {
    if (plans.length === 0) {
      return { halfW: 500, halfH: 500, center: new THREE.Vector3() };
    }

    const box = new THREE.Box3();
    plans.forEach(obj => box.expandByObject(obj));

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    let extraPadding = padding;
    if (plans.length === 1 && plans[0].userData['plan']) {
      const p = plans[0].userData['plan'];
      if (p.shape && (p.shape.shapeType === ShapeTypeEnum.Angle || p.shape.shapeType === ShapeTypeEnum.Circle)) {
        extraPadding = 3.0;
      }
    }

    const worldW = (size.x || 1000) * extraPadding;
    const worldH = (size.y || 1000) * extraPadding;

    const vwPx = this.container?.clientWidth  || 800;
    const vhPx = this.container?.clientHeight || 600;
    const aspect = vwPx / vhPx;

    // Choose half-sizes that respect the aspect ratio while fitting the scene
    let halfH = worldH / 2;
    let halfW = halfH * aspect;
    if (halfW < worldW / 2) {
      halfW = worldW / 2;
      halfH = halfW / aspect;
    }

    return { halfW, halfH, center };
  }

  /**
   * Returns the visible world-space height at the current zoom level.
   * Works for both OrthographicCamera (2D) and PerspectiveCamera (3D).
   */
  public getVisibleWorldHeight(): number {
    if (this.camera instanceof THREE.OrthographicCamera) {
      return (this.camera.top - this.camera.bottom) / this.camera.zoom;
    } else if (this.camera instanceof THREE.PerspectiveCamera) {
      const fovRad = THREE.MathUtils.degToRad(this.camera.fov);
      return 2 * Math.tan(fovRad / 2) * this.camera.position.z;
    }
    return 1000; // fallback
  }

  public toScreenPosition(objPos: THREE.Vector3): { x: number, y: number } {
    if (!this.container) return { x: 0, y: 0 };
    const vector = objPos.clone();
    vector.project(this.camera);
    const widthHalf  = this.container.clientWidth  / 2;
    const heightHalf = this.container.clientHeight / 2;
    return {
      x: (vector.x *  widthHalf) + widthHalf,
      y: -(vector.y * heightHalf) + heightHalf
    };
  }

  // ---------------------------------------------------------------------------
  // Snapshot
  // ---------------------------------------------------------------------------

  public downloadSnapshot(node: HTMLElement): void {
    htmlToImage.toPng(node, { backgroundColor: '#ffffff' })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = 'plan-2d-snapshot.png';
        link.href = dataUrl;
        link.click();
      })
      .catch((error) => {
        console.error('Snapshot failed', error);
      });
  }

  // ---------------------------------------------------------------------------
  // 3D Mode Transitions
  // ---------------------------------------------------------------------------

  public enable3DMode(renderLoopCallback: () => void) {
    if (this.is3DMode || this.isTransitioning) return;

    // Compute scene bounds
    const box = new THREE.Box3();
    let hasObjects = false;
    this.scene.children.forEach(c => {
      if (c.userData?.['plan']) { box.expandByObject(c); hasObjects = true; }
    });
    if (!hasObjects) box.set(new THREE.Vector3(-100, -100, 0), new THREE.Vector3(100, 100, 0));

    const size   = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    const radius = size.length() / 2;
    this.debugRadius = radius;
    this.debugCenter = center.clone();

    // Build / reuse perspective camera
    if (!this.perspCamera) {
      const aspect = (this.container?.clientWidth || 800) / (this.container?.clientHeight || 600);
      this.perspCamera = new THREE.PerspectiveCamera(45, aspect, 10, 200000);
    } else {
      const aspect = (this.container?.clientWidth || 800) / (this.container?.clientHeight || 600);
      this.perspCamera.aspect = aspect;
    }

    const fovRad   = THREE.MathUtils.degToRad(this.perspCamera.fov);
    const distance = (radius * 1.5) / Math.tan(fovRad / 2);
    this.debugHeight = distance;
    this.perspCamera.far = distance * 10;

    // Copy ortho camera orientation as starting point
    this.perspCamera.position.copy(this.orthoCamera.position);
    this.perspCamera.up.copy(this.orthoCamera.up);
    this.perspCamera.lookAt(center);
    this.perspCamera.updateProjectionMatrix();
    this.perspCamera.updateMatrixWorld();

    // Switch active camera to perspective
    this.camera = this.perspCamera;

    const targetPosition = new THREE.Vector3(
      this.debugCenter.x,
      this.debugCenter.y - this.debugRadius,
      this.debugHeight
    );
    const targetUp  = new THREE.Vector3(0, 0, 1);
    const targetFar = distance * 10;

    this.animateCameraTransition(
      targetPosition, this.debugCenter, targetUp, targetFar, 800, renderLoopCallback
    ).then(() => {
      this.is3DMode = true;
      this.debugAngle = Math.PI;
    });
  }

  public disable3DMode(renderLoopCallback: () => void) {
    this.is3DMode = false;
    if (this.isTransitioning) return;

    // Collect plans for zoomToFit
    const plans: THREE.Object3D[] = [];
    this.scene.children.forEach(c => {
      if (c.userData?.['plan']) plans.push(c);
    });

    const { halfW, halfH, center } = this.getFitOrthoParams(plans, 1.5);

    // Reset ortho camera to match target view
    this.orthoCamera.left   = -halfW;
    this.orthoCamera.right  =  halfW;
    this.orthoCamera.top    =  halfH;
    this.orthoCamera.bottom = -halfH;
    this.orthoCamera.zoom   = 1;
    this.orthoCamera.updateProjectionMatrix();

    // Animate perspective camera back towards the top-down position, then switch back
    const targetPos    = new THREE.Vector3(center.x, center.y, 1000);
    const targetUp     = new THREE.Vector3(0, 1, 0);
    const targetLookAt = new THREE.Vector3(center.x, center.y, 0);

    this.animateCameraTransition(
      targetPos, targetLookAt, targetUp, 10000, 800, renderLoopCallback
    ).then(() => {
      // Switch back to ortho camera
      this.camera = this.orthoCamera;
      this.orthoCamera.position.set(center.x, center.y, 1000);
      this.orthoCamera.lookAt(center.x, center.y, 0);
      this.orthoCamera.updateProjectionMatrix();
      this.orthoCamera.updateMatrixWorld();

      if (renderLoopCallback) renderLoopCallback();
    });
  }

  // ---------------------------------------------------------------------------
  // Camera Transition (works on whatever `this.camera` is)
  // ---------------------------------------------------------------------------

  private animateCameraTransition(
    targetPos: THREE.Vector3,
    targetLookAt: THREE.Vector3,
    targetUp: THREE.Vector3,
    targetFar: number,
    duration: number,
    renderCallback: () => void
  ): Promise<void> {
    return new Promise((resolve) => {
      this.isTransitioning = true;
      const cam = this.camera;

      const startPos  = cam.position.clone();
      const startUp   = cam.up.clone();
      const startFar  = (cam as any).far ?? 10000;
      const startQuat = cam.quaternion.clone();

      // Compute target quaternion via a dummy camera
      const dummy = cam.clone() as THREE.PerspectiveCamera;
      dummy.position.copy(targetPos);
      dummy.up.copy(targetUp);
      dummy.lookAt(targetLookAt);
      dummy.updateMatrix();
      const targetQuat = dummy.quaternion.clone();

      const startTime = performance.now();

      const loop = (time: number) => {
        const elapsed  = time - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const t        = 1 - Math.pow(1 - progress, 3); // cubic ease-out

        cam.position.lerpVectors(startPos, targetPos, t);
        cam.up.lerpVectors(startUp, targetUp, t);
        cam.quaternion.slerpQuaternions(startQuat, targetQuat, t);

        if ((cam as any).far !== undefined) {
          (cam as any).far = startFar + (targetFar - startFar) * t;
        }
        if ((cam as any).updateProjectionMatrix) {
          (cam as any).updateProjectionMatrix();
        }

        if (renderCallback) renderCallback();
        this.renderer.render(this.scene, cam);

        if (progress < 1) {
          requestAnimationFrame(loop);
        } else {
          this.isTransitioning = false;
          resolve();
        }
      };
      requestAnimationFrame(loop);
    });
  }

  // ---------------------------------------------------------------------------
  // 3D orbit loop
  // ---------------------------------------------------------------------------

  private animate3DLogic() {
    if (!this.perspCamera) return;
    this.debugAngle += 0.005;
    const x = this.debugCenter.x + this.debugRadius * Math.sin(this.debugAngle);
    const y = this.debugCenter.y + this.debugRadius * Math.cos(this.debugAngle);
    const z = this.debugHeight;

    this.perspCamera.position.set(x, y, z);
    this.perspCamera.up.set(0, 0, 1);
    this.perspCamera.lookAt(this.debugCenter);
    this.perspCamera.updateProjectionMatrix();
  }
}
